<script setup lang="ts">
import { computed, ref, toRefs, useId, useTemplateRef, watch } from 'vue';

const inputDomId = useId();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  commit: [value: string];
}>();

const props = defineProps<{
  modelValue: string;
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  maxlength?: number;
  hasErrors?: boolean;
  inputClass?: string;
}>();
const { modelValue, label, maxlength, hasErrors } = toRefs(props);

const modelValueInternal = ref<string>('');
const isFocused = ref(false);
const inputElRef = useTemplateRef('inputElRef');
let skipNextBlurCommit = false;

const isLabelFloating = computed(() => {
  return isFocused.value || modelValueInternal.value.length > 0;
});

const handleInput = (event: Event): void => {
  emit('update:modelValue', (event.target as HTMLInputElement).value);
};

const handleEnter = (): void => {
  skipNextBlurCommit = true;
  emit('commit', modelValueInternal.value);
  inputElRef.value?.blur();
  setTimeout(() => {
    skipNextBlurCommit = false;
  }, 0);
};

const handleBlur = (): void => {
  if (skipNextBlurCommit) {
    skipNextBlurCommit = false;
    return;
  }
  emit('commit', modelValueInternal.value);
};

watch(
  modelValue,
  () => {
    modelValueInternal.value = modelValue.value;
  },
  { immediate: true },
);
const focus = (): void => {
  inputElRef.value?.focus();
};
const select = (): void => {
  inputElRef.value?.select();
};
defineExpose({ focus, select });
</script>

<template>
  <div class="relative">
    <label
      v-if="label"
      :for="inputDomId"
      class="input-text-label transition-all duration-200 pointer-events-none"
      :class="{
        'label-floating': isLabelFloating,
        'text-tint': isFocused,
      }"
    >
      {{ label }}
    </label>
    <input
      :id="inputDomId"
      ref="inputElRef"
      :class="[
        'input-text h-full',
        disabled && 'input-text-disabled',
        label && 'input-text-labeled',
        hasErrors && 'outline-inner-error',
        inputClass,
      ]"
      :value="modelValueInternal"
      :placeholder="!label ? placeholder : ''"
      :disabled="disabled"
      :maxlength="maxlength"
      @input="handleInput"
      @focus="isFocused = true"
      @blur="
        isFocused = false;
        handleBlur();
      "
      @keydown.enter.prevent="handleEnter"
    />
  </div>
</template>
